import { Immutable } from "immer";
import React from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductSelectionGridSSR } from "tsi-common-react/src/apps/product-compare/ProductSelectionGridSSR/index";
import { ProductGridSSR } from "tsi-common-react/src/apps/product-grid2/ProductGridSSR/index";
import { RetailCardsSSR } from "tsi-common-react/src/apps/retail/RetailCardsSSR/index";
import { AccordionBlock } from "tsi-common-react/src/components/AccordionBlock/index";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";
import { ColumnsCarouselBlock } from "tsi-common-react/src/components/ColumnsCarouselBlock/index";
import { ComparisonBlock } from "tsi-common-react/src/components/ComparisonBlock/index";
import { CompatibilityAccordion } from "tsi-common-react/src/components/CompatibilityAccordion/index";
import { CompetitionCompareTableBlock } from "tsi-common-react/src/components/CompetitionCompareTableBlock/index";
import { CountdownTimer } from "tsi-common-react/src/components/CountdownTimer/index";
import { EmailCaptureContent } from "tsi-common-react/src/components/EmailCaptureContent/index";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { FeaturesBlock } from "tsi-common-react/src/components/FeaturesBlock/index";
import { GalleryBlock } from "tsi-common-react/src/components/GalleryBlock/index";
import { HomepageHeroPromoBox } from "tsi-common-react/src/components/HomepageHeroPromoBox/index";
import { LimitedTimeOfferGridBlock } from "tsi-common-react/src/components/LimitedTimeOfferGridBlock/index";
import { PreApprovalBlock } from "tsi-common-react/src/components/PreApprovalBlock/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";
import { ProductComparePrintOnlyBlock } from "tsi-common-react/src/components/ProductComparePrintOnlyBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { TSIAlignedRichTextBlock } from "tsi-common-react/src/components/TSIAlignedRichTextBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import {
    CountdownTimerTheme,
    type ICountdownTimer,
} from "tsi-common-react/src/models/countdown-timer";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { templates } from "@reactivated";
import type {
    CompatibilityAccordionModule as CompatibilityAccordionValue,
    CountdownTimerBlock as CountdownTimerBlockValue,
    EmailCaptureBlock as EmailCaptureBlockValue,
    FullReviewsBlock as FullReviewsBlockValue,
    GalleryBlock as GalleryBlockValue,
    CocoonHomeCTABlock as HomeCTABlockValue,
    HomeChartBlock as HomeChartBlockValue,
    HomeReviewsAndMoreBlock as HomeReviewsAndMoreBlockValue,
    ImageChooserBlock,
    MattressChartBlock as MattressChartBlockValue,
    OprahBlock as OprahBlockValue,
    PromiseBlock as PromiseBlockValue,
    CocoonPromoConditionalBlock as PromoConditionalBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    ReviewsBlock as ReviewsBlockValue,
    RichTextBlock as RichTextBlockValue,
    ValuePropBarBlock as SharedValuePropBarBlockValue,
    VideoBarBlock as VideoBarBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { CocoonFooterBlock } from "../components/CocoonFooterBlock";
import { CocoonHomeCTABlock } from "../components/CocoonHomeCTABlock";
import { CocoonHomeChartBlock } from "../components/CocoonHomeChartBlock";
import { CocoonMattressChartBlock } from "../components/CocoonMattressChartBlock";
import { CocoonPDPCarouselBlock } from "../components/CocoonPDPCarouselBlock";
import { CocoonPromiseBlock } from "../components/CocoonPromiseBlock";
import { CocoonReviewsBlock } from "../components/CocoonReviewsBlock";
import { CocoonSingleReviewBlock } from "../components/CocoonSingleReviewBlock";
import { CocoonValuePropListBlock } from "../components/CocoonValuePropListBlock";
import { CocoonVideoBarBlock } from "../components/CocoonVideoBarBlock";
import { CompareHeroBlock } from "../components/CompareHeroBlock";
import { CtaSectionBlock } from "../components/CtaSectionBlock";
import { CustomerSupportCardsBlock } from "../components/CustomerSupportCardsBlock";
import { DeliveredInBoxBlock } from "../components/DeliveredInBoxBlock";
import { FinancingOfferBlock } from "../components/FinancingOfferBlock";
import { FoundationBuildBlock } from "../components/FoundationBuildBlock";
import { FoundationSpecsBlock } from "../components/FoundationSpecsBlock";
import { CocoonFullReviewsBlock } from "../components/FullReviewsBlock";
import { GetOffModalBlock } from "../components/GetOffModalBlock";
import { HeroWithBannerBlock } from "../components/HeroWithBannerBlock";
import { HighlightBarBlock } from "../components/HighlightBarBlock";
import { HomeOprahBlock } from "../components/HomeOprahBlock";
import { HomeOwnerSayingsBlock } from "../components/HomeOwnerSayingsBlock";
import { HomePromiseBlock } from "../components/HomePromiseBlock";
import { HomeReviewsAndMoreBlock } from "../components/HomeReviewsAndMoreBlock";
import { LimitedOfferBlock } from "../components/LimitedOfferBlock";
import { MattressDifferenceBlock } from "../components/MattressDifferenceBlock";
import { MattressFeelBlock } from "../components/MattressFeelBlock";
import { MattressSelectBlock } from "../components/MattressSelectBlock";
import { MultiProductBlock } from "../components/MultiProductBlock";
import { MultiProductInfoBlock } from "../components/MultiProductInfoBlock";
import { OprahBlock } from "../components/OprahBlock";
import { OprahModalBlock } from "../components/OprahModalBlock";
import { PillowValuePropsContainer } from "../components/PillowValuePropsContainer";
import { PreQualificationBlock } from "../components/PreQualificationBlock";
import { ProductBundlesBlock } from "../components/ProductBundlesBlock";
import { ReviewsRightBarSection } from "../components/ReviewsRightBarSection";
import { ShippingEstimationBarBlock } from "../components/ShippingEstimationBarBlock";
import { SideBySideBlock } from "../components/SideBySideBlock";
import { SimpleProductBlock } from "../components/SimpleProductBlock";
import { TechSpecBlock } from "../components/TechSpecBlock";
import { TestimonialBlock } from "../components/TestimonialBlock";
import { WarrantyBlock } from "../components/WarrantyBlock";

const getStaticURL = (staticURL: string, path: string): string =>
    `${staticURL}${path}`;

const getImageAlt = (
    image: Immutable<ImageChooserBlock> | null | undefined,
): string => image?.default_alt_text || image?.title || "";

const RICH_TEXT_WRAP_START = '<div class="rich-text">';
const RICH_TEXT_WRAP_END = "</div>";
const unwrapRichTextBlock = (value: string): string =>
    value.startsWith(RICH_TEXT_WRAP_START) && value.endsWith(RICH_TEXT_WRAP_END)
        ? value.slice(RICH_TEXT_WRAP_START.length, -RICH_TEXT_WRAP_END.length)
        : value;

const GALLERY_DEFAULT_THUMBNAIL_PATH = "tsicommon_cms/img/video-thumbnail.jpg";

const HomeCTABlock = (
    block: Immutable<{ value: HomeCTABlockValue }>,
): React.JSX.Element => <CocoonHomeCTABlock value={block.value} />;

const HomeChartBlock = (
    block: Immutable<{ value: HomeChartBlockValue }>,
): React.JSX.Element => <CocoonHomeChartBlock value={block.value} />;

const HomeReviewsAndMoreBlockEntry = (
    block: Immutable<{ value: HomeReviewsAndMoreBlockValue }>,
): React.JSX.Element => <HomeReviewsAndMoreBlock value={block.value} />;

type PageContext = {
    pageSlug?: string | null;
    pageExtraClasses?: string | null;
    isProductPageBody?: boolean;
    homeOprahChillUrl?: string | null;
    homeOprahClassicUrl?: string | null;
};

const getPageClassName = (context?: PageContext): string =>
    context?.pageSlug ?? "";

const MattressChartBlock = (
    block: Immutable<{ value: MattressChartBlockValue }> & PageContext,
): React.JSX.Element => (
    <CocoonMattressChartBlock
        value={block.value}
        page_slug={getPageClassName(block)}
        page_extra_classes={block.pageExtraClasses ?? ""}
    />
);

const HomeOprahBlockEntry = (
    block: Immutable<{ value: OprahBlockValue }> & PageContext,
): React.JSX.Element => (
    <HomeOprahBlock
        value={block.value}
        chillUrl={block.homeOprahChillUrl ?? ""}
        classicUrl={block.homeOprahClassicUrl ?? ""}
    />
);

const PromiseBlock = (
    block: Immutable<{ value: PromiseBlockValue }>,
): React.JSX.Element => <CocoonPromiseBlock value={block.value} />;

const ReviewsBlock = (
    block: Immutable<{ value: ReviewsBlockValue }> & PageContext,
): React.JSX.Element => (
    <CocoonReviewsBlock
        value={block.value}
        page_slug={getPageClassName(block)}
        isProductPageBody={block.isProductPageBody}
    />
);

const VideoBarBlock = (
    block: Immutable<{ value: VideoBarBlockValue }>,
): React.JSX.Element => {
    const staticURL = useStaticURL();
    const videos = [
        {
            start: "0",
            image: getStaticURL(staticURL, "img/pdp/moving.jpg"),
            alt: "Watch Moving",
            caption: "Moving the box",
        },
        {
            start: "10",
            image: getStaticURL(staticURL, "img/pdp/unboxing.jpg"),
            alt: "Watch Unboxing",
            caption: "Unpacking the box",
        },
        {
            start: "24",
            image: getStaticURL(staticURL, "img/pdp/unrolling.jpg"),
            alt: "Watch Unrolling",
            caption: "Unrolling the mattress",
        },
        {
            start: "47",
            image: getStaticURL(staticURL, "img/pdp/feel.jpg"),
            alt: "Watch Feel",
            caption: "trying out the mattress",
        },
    ];
    return <CocoonVideoBarBlock value={block.value} videos={videos} />;
};

const CountdownTimerBlock = (
    block: Immutable<{ value: CountdownTimerBlockValue }>,
): React.JSX.Element | null => {
    const timerQueue = block.value.timer_queue;
    if (!timerQueue?.is_active) {
        return null;
    }
    const timerData: ICountdownTimer[] = timerQueue.timers.map((timer) => ({
        body: timer.body,
        mobile_body: timer.mobile_body,
        start_date: timer.start_date,
        end_date: timer.end_date,
        theme: timer.theme as CountdownTimerTheme,
    }));
    return (
        <section
            className={concatClassNames([
                "countdown-timer-block",
                block.value.responsive_visibility?.visibility ?? "",
            ])}
        >
            <div className="main-container">
                <div className="countdown-timer-container">
                    <CountdownTimer
                        timerData={timerData}
                        timersExpiredPromo=""
                    />
                </div>
            </div>
        </section>
    );
};

const CompatibilityBlock = (
    block: Immutable<{ value: CompatibilityAccordionValue }>,
): React.JSX.Element | null => {
    if (!block.value.compatibility_panels.length) {
        return null;
    }
    return (
        <section className={block.value.background_style.style}>
            <CompatibilityAccordion value={block.value} />
        </section>
    );
};

const RichTextBlockEntry = (
    block: Immutable<{ value: RichTextBlockValue }>,
): React.JSX.Element => (
    <div
        className="rich-text"
        dangerouslySetInnerHTML={{
            __html: unwrapRichTextBlock(block.value),
        }}
    />
);

const RawHTMLBlockEntry = (
    block: Immutable<{ value: RawHTMLBlockValue }>,
): React.JSX.Element => <RawHTMLBlock value={block.value} />;

const GalleryBlockEntry = (block: {
    value: Immutable<GalleryBlockValue>;
    initialGalleryIndex?: number;
}): React.JSX.Element => {
    const staticURL = useStaticURL();
    const galleries = (block.value.galleries ?? []).filter(
        (gallery) => gallery.gallery !== null,
    );
    const defaultIndex = galleries.findIndex(
        (gallery) => !gallery.product && !(gallery.attributes ?? []).length,
    );
    const initialGalleryIndex =
        block.initialGalleryIndex ??
        block.value.initial_gallery_index ??
        (defaultIndex >= 0 ? defaultIndex : 0);

    return (
        <GalleryBlock
            blockData={{ ...block.value, galleries }}
            defaultThumbnail={`${staticURL}${GALLERY_DEFAULT_THUMBNAIL_PATH}`}
            initialGalleryIndex={initialGalleryIndex}
        />
    );
};

const EmailCaptureBlockEntry = (block: {
    value: Immutable<EmailCaptureBlockValue>;
}): React.JSX.Element | null => {
    const snippet = block.value.email_snippet;
    if (!snippet) {
        return null;
    }
    return (
        <section
            className={concatClassNames([
                "email-capture-block",
                block.value.background_style?.style ?? "",
                block.value.responsive_visibility?.visibility ?? "",
            ])}
        >
            <EmailCaptureContent
                content={{
                    email_snippet: {
                        title: snippet.title ?? "",
                        content: snippet.content,
                        confirm_content: snippet.confirm_content,
                        image: snippet.image,
                        footnotes: snippet.footnotes,
                    },
                }}
            />
        </section>
    );
};

const SharedValuePropBarBlock = (
    block: Immutable<{ value: SharedValuePropBarBlockValue }>,
): React.JSX.Element => <ValuePropsBarBlock valuePropsData={block.value} />;

const buildToolboxComponentsMap = (context: PageContext = {}) => {
    const map = {
        accordion: AccordionBlock,
        aligned_rich_text_block: TSIAlignedRichTextBlock,
        banner_block: BannerBlock,
        columns_carousel_block: ColumnsCarouselBlock,
        compatibility: CompatibilityBlock,
        competition_compare_table_block: CompetitionCompareTableBlock,
        countdown_timer: CountdownTimerBlock,
        cta_section_block: CtaSectionBlock,
        customer_support_cards: CustomerSupportCardsBlock,
        delivered_in_box_block: DeliveredInBoxBlock,
        email_capture: EmailCaptureBlockEntry,
        faqs_block: FAQsBlock,
        full_reviews_block: (block: {
            value: Immutable<FullReviewsBlockValue>;
        }) => <CocoonFullReviewsBlock value={block.value} />,
        highlight_bar_block: HighlightBarBlock,
        html: RawHTMLBlockEntry,
        home_cta_block: HomeCTABlock,
        homepage_hero_promo_box: HomepageHeroPromoBox,
        mattress_chart_block: (block: {
            value: Immutable<MattressChartBlockValue>;
        }) => <MattressChartBlock {...block} {...context} />,
        mattress_difference_block: MattressDifferenceBlock,
        multi_product_info_block: MultiProductInfoBlock,
        pre_approval_block: PreApprovalBlock,
        product_callout_block: ProductCalloutBlock,
        product_grid: (block: {
            value: Immutable<templates.ProductGridBlockTemplate> | null;
        }) => (block.value ? <ProductGridSSR {...block.value} /> : null),
        product_selection_grid: (block: {
            value: Immutable<templates.ProductSelectionGridBlockTemplate> | null;
        }) =>
            block.value ? <ProductSelectionGridSSR {...block.value} /> : null,
        promise_block: PromiseBlock,
        promotion_block: (block: {
            index?: number;
            value: Immutable<templates.PromotionBlockTemplate["value"]>;
        }) => (
            <PromotionBlock
                value={block.value}
                blockIndex={(block.index ?? 0) + 1}
            />
        ),
        reviews_block: (block: { value: Immutable<ReviewsBlockValue> }) => (
            <ReviewsBlock {...block} {...context} />
        ),
        rich_text: RichTextBlockEntry,
        side_by_side_block: SideBySideBlock,
        testimonial_block: TestimonialBlock,
        value_prop_bar_block: CocoonValuePropListBlock,
        video_bar_block: VideoBarBlock,
        warranty_block: WarrantyBlock,
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                <StreamField
                    tagName={React.Fragment}
                    value={block.value.content ?? []}
                    components={map}
                />
            </PromoConditionalBlock>
        ),
        retail_cards: () => <RetailCardsSSR />,
    } as const;
    return map;
};

const toolboxComponentsMap = buildToolboxComponentsMap();

export const buildContextualToolboxComponentsMap = buildToolboxComponentsMap;

export const buildHomePageComponentsMap = (context: PageContext = {}) => ({
    ...buildToolboxComponentsMap(context),
    home_chart_block: HomeChartBlock,
    oprah: (block: { value: Immutable<OprahBlockValue> }) => (
        <HomeOprahBlockEntry {...block} {...context} />
    ),
    owner_sayings: HomeOwnerSayingsBlock,
    prequal_block: PreQualificationBlock,
    product_bundles_block: ProductBundlesBlock,
    promises: HomePromiseBlock,
    reviews_and_more: HomeReviewsAndMoreBlockEntry,
    mattress_select: MattressSelectBlock,
    multi_product_block: MultiProductBlock,
    simple_product_block: SimpleProductBlock,
    single_review: CocoonSingleReviewBlock,
    value_prop_bar: SharedValuePropBarBlock,
});

export const offersPageComponentsMap = {
    ...toolboxComponentsMap,
    financing_offers: FinancingOfferBlock,
    product_offers: LimitedOfferBlock,
    limited_time_offer_grid_block: LimitedTimeOfferGridBlock,
} as const;

export const compareHeroComponentsMap = {
    hero: CompareHeroBlock,
    hero_with_banner: HeroWithBannerBlock,
} as const;

export const buildComparePageBodyComponentsMap = (
    context: PageContext = {},
) => ({
    ...buildToolboxComponentsMap(context),
    heading: (block: { value: Immutable<string> }) => <h2>{block.value}</h2>,
    paragraph: RichTextBlockEntry,
    image: (block: { value: Immutable<ImageChooserBlock> }) => (
        <img
            src={block.value.url}
            alt={getImageAlt(block.value)}
            loading="lazy"
            decoding="async"
        />
    ),
    mattress_chart: (block: { value: Immutable<MattressChartBlockValue> }) => (
        <MattressChartBlock {...block} {...context} />
    ),
    promise: PromiseBlock,
    delivered_in_box: DeliveredInBoxBlock,
    reviews: (block: { value: Immutable<ReviewsBlockValue> }) => (
        <ReviewsBlock {...block} {...context} />
    ),
    warranty: WarrantyBlock,
    video_bar: VideoBarBlock,
    oprah: OprahBlock,
    mattress_difference: MattressDifferenceBlock,
    mattress_feel: MattressFeelBlock,
    highlight_bar: HighlightBarBlock,
    cta_section_upper: CtaSectionBlock,
    cta_section_lower: CtaSectionBlock,
    shipping_estimation_bar: ShippingEstimationBarBlock,
    comparison_block: ComparisonBlock,
    multi_product_info_block: MultiProductInfoBlock,
    competition_compare_table: CompetitionCompareTableBlock,
    testimonal: TestimonialBlock,
    side_by_side: SideBySideBlock,
    html: RawHTMLBlockEntry,
});

export const buildProductPageBaseComponentsMap = (
    context: PageContext = {},
) => ({
    ...buildToolboxComponentsMap(context),
    carousel: CocoonPDPCarouselBlock,
    foundation_build: FoundationBuildBlock,
    foundation_specs: FoundationSpecsBlock,
    gallery_block: GalleryBlockEntry,
    heading: (block: { value: Immutable<string> }) => <h2>{block.value}</h2>,
    image: (block: { value: Immutable<ImageChooserBlock> }) => (
        <img
            src={block.value.url}
            alt={getImageAlt(block.value)}
            loading="lazy"
            decoding="async"
        />
    ),
    mattress_feel: MattressFeelBlock,
    oprah: OprahBlock,
    pillow_value_props: PillowValuePropsContainer,
    product_compare_grid: (block: {
        value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
    }) => (block.value ? <ProductCompareGridSSR {...block.value} /> : null),
    single_review: CocoonSingleReviewBlock,
    tech_specs: TechSpecBlock,
    value_prop_bar: SharedValuePropBarBlock,
    video_bar: VideoBarBlock,
    change_root_option_block: () => null,
    future_block: FeaturesBlock,
    root_product_conditional_block: () => null,
    shipping_estimation_bar: ShippingEstimationBarBlock,
});

export const buildProductComparePageComponentsMap = (
    context: PageContext = {},
) => ({
    ...buildToolboxComponentsMap(context),
    richtext: RichTextBlockEntry,
    print_only_richtext: ProductComparePrintOnlyBlock,
    compare_grid: (block: {
        value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
    }) => (block.value ? <ProductCompareGridSSR {...block.value} /> : null),
});

export const financingPageComponentsMap = {
    heading: (block: { value: Immutable<string> }) => <h2>{block.value}</h2>,
    paragraph: RichTextBlockEntry,
    html: RawHTMLBlockEntry,
    hidden_anchor_link: (block: { value: Immutable<string> }) => (
        <span id={block.value}></span>
    ),
    promotion: (block: {
        index?: number;
        value: Immutable<templates.PromotionBlockTemplate["value"]>;
    }) => (
        <PromotionBlock
            value={block.value}
            blockIndex={(block.index ?? 0) + 1}
        />
    ),
} as const;

export const faqPageComponentsMap = {
    html: RawHTMLBlockEntry,
    customer_support_cards_block: CustomerSupportCardsBlock,
} as const;

export const reviewsRightBarComponentsMap = {
    right_bar_section: ReviewsRightBarSection,
    html: RawHTMLBlockEntry,
} as const;

export const footerComponentsMap = {
    footer: CocoonFooterBlock,
    hidden_anchor_link: (block: { value: Immutable<string> }) => (
        <span id={block.value}></span>
    ),
} as const;

export const configuratorPromoLineComponentsMap = {
    rich_text: RichTextBlockEntry,
    html_block: RawHTMLBlockEntry,
} as const;

export const promoModalComponentsMap = {
    oprah: OprahModalBlock,
    getOff: GetOffModalBlock,
    rawhtml: RawHTMLBlockEntry,
} as const;
